import { useCallback, useMemo, useState } from 'react';

import { cn, iaRa } from 'lib/helpers/utility';

import MoreMenu from '../MoreMenu/MoreMenu';
import { withErrorBoundary } from '../Error/Boundary';
import HorizontalScroll from '../HorizontalScroll/HorizontalScroll';

import ControlItem from './components/ControlItem';
import { getWidthSignature } from './lib/overflow';
import useOverflowItems from './lib/useOverflowItems';
import ControlLinkItem from './components/ControlLinkItem';

const SegmentedControl = ({
  items,
  onClick = () => {},
  active,
  disabled = false,
  count,
  horizontalScroll = false,
  disableOverflow = false,
  moreItems = 'all',
  loading,
  size = 'default',
  className,
  itemClassName,
  trailing
}) => {
  const [selected, setSelected] = useState(active);
  const activeItem = active ?? selected;
  const safeItems = iaRa(items);

  const signature = useMemo(
    () => getWidthSignature({ items: safeItems, activeValue: activeItem, count, loading }),
    [safeItems, activeItem, count, loading]
  );

  const { containerRef, visibleItems, hiddenItems } = useOverflowItems({
    items: safeItems,
    activeValue: activeItem,
    signature,
    enabled: !horizontalScroll && !disableOverflow
  });

  const handleClick = useCallback(
    (item) => {
      if (disabled || item.disabled || item.value === activeItem) return;

      setSelected(item.value);
      onClick(item);
      item.onClick?.(item);
    },
    [disabled, activeItem, onClick]
  );

  const renderItem = (item) =>
    item.to ? (
      <ControlLinkItem
        key={item.value}
        item={item}
        isActive={activeItem === item.value}
        size={size}
        className={itemClassName}
      />
    ) : (
      <ControlItem
        key={item.value}
        item={item}
        isActive={activeItem === item.value}
        disabled={Boolean(disabled || item.disabled)}
        onClick={handleClick}
        count={count}
        loading={loading}
        size={size}
        className={itemClassName}
      />
    );

  if (!safeItems.length) return null;

  if (horizontalScroll) {
    return (
      <HorizontalScroll className={cn('rounded-md', disabled && 'cursor-not-allowed', className)}>
        {visibleItems.map(renderItem)}
      </HorizontalScroll>
    );
  }

  return (
    <div
      ref={containerRef}
      className={cn(
        'h-8 w-full max-w-fit grow gap-0.5 bg-[#F3F4F5] p-0.5 xs:h-auto xs:min-h-8',
        'flex items-center rounded-md',
        'shadow-[inset_0px_0px_4px_rgba(55,65,69,0.08)]',
        size === 'small' && 'h-7 xs:min-h-7',
        size === 'large' && 'h-11 gap-1',
        disabled && 'cursor-not-allowed',
        className
      )}>
      {visibleItems.map(renderItem)}
      {hiddenItems.length > 0 && (
        <MoreMenu
          items={moreItems === 'hidden' ? hiddenItems : safeItems}
          activeValue={activeItem}
          onSelect={handleClick}
          className={cn('shrink-0', size === 'small' ? 'h-6' : 'h-7')}
          buttonClassName={size === 'small' ? '!h-6 !min-h-6' : undefined}
        />
      )}
      {trailing}
    </div>
  );
};

/**
 * @type {import('react').ForwardRefExoticComponent<import('./lib/propTypes').SegmentedControlProps & import('react').RefAttributes<HTMLElement>>}
 */

export default withErrorBoundary(SegmentedControl);
